import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { techniqueCuesApi } from '../api/techniqueCues';

export default function TechniqueCueEditor({
  open, onOpenChange, exerciseId, exerciseName, clientId, clientName,
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [isOverride, setIsOverride] = useState(!!clientId);
  const [textEn, setTextEn] = useState('');
  const [textPt, setTextPt] = useState('');

  const { data: current } = useQuery({
    queryKey: ['technique-cue-editor', exerciseId, isOverride ? clientId : null],
    queryFn: async () => {
      const { data } = await techniqueCuesApi.resolve(
        exerciseId, isOverride ? clientId : null,
      );
      return data.cue;
    },
    enabled: open && !!exerciseId,
  });

  useEffect(() => {
    setTextEn(current?.text_en || '');
    setTextPt(current?.text_pt || '');
  }, [current]);

  const saveMutation = useMutation({
    mutationFn: () => techniqueCuesApi.upsert({
      exercise_id: exerciseId,
      client_id: isOverride ? clientId : null,
      text_en: textEn.trim(),
      text_pt: textPt.trim(),
    }),
    onSuccess: () => {
      toast.success(t('techniqueCues.saved'));
      qc.invalidateQueries({ queryKey: ['technique-cue', exerciseId] });
      qc.invalidateQueries({ queryKey: ['technique-cue-library'] });
      onOpenChange(false);
    },
    onError: () => toast.error(t('techniqueCues.saveError')),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('techniqueCues.editorTitle', { exercise: exerciseName })}</DialogTitle>
        </DialogHeader>
        {clientId && (
          <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2">
            <div className="text-sm">
              <div className="font-semibold">{t('techniqueCues.overrideFor', { name: clientName })}</div>
              <div className="text-xs text-muted-foreground">
                {isOverride ? t('techniqueCues.overrideHelp') : t('techniqueCues.globalHelp')}
              </div>
            </div>
            <Switch checked={isOverride} onCheckedChange={setIsOverride} />
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label htmlFor="cue-en">{t('techniqueCues.textEn')}</Label>
            <Textarea
              id="cue-en" value={textEn}
              onChange={(e) => setTextEn(e.target.value)}
              rows={3} maxLength={500}
              placeholder={t('techniqueCues.textPlaceholderEn')}
            />
          </div>
          <div>
            <Label htmlFor="cue-pt">{t('techniqueCues.textPt')}</Label>
            <Textarea
              id="cue-pt" value={textPt}
              onChange={(e) => setTextPt(e.target.value)}
              rows={3} maxLength={500}
              placeholder={t('techniqueCues.textPlaceholderPt')}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
